import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  getProductThumbUrl,
  peekProductThumbUrl,
  productImageVersion,
} from '../../utils/productThumbCache';

type Props = {
  productId: number;
  hasImage?: boolean;
  updatedAt?: string | null;
  className?: string;
  children?: ReactNode;
};

function ProductCardThumb({ productId, hasImage, updatedAt, className, children }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const version = productImageVersion(updatedAt);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState<string | null>(() =>
    hasImage ? peekProductThumbUrl(productId, version) : null
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const cached = hasImage ? peekProductThumbUrl(productId, version) : null;
    setUrl(cached);
    setFailed(false);
    setVisible(Boolean(cached));
  }, [productId, version, hasImage]);

  useEffect(() => {
    if (!hasImage) return;
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '160px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasImage, productId, version]);

  useEffect(() => {
    if (!hasImage || !visible || failed) return;
    const cached = peekProductThumbUrl(productId, version);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    void getProductThumbUrl(productId, version)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hasImage, visible, productId, version, failed]);

  return (
    <div ref={rootRef} className={className}>
      {url ? (
        <img className="product-card__thumb" src={url} alt="" loading="lazy" />
      ) : (
        <span className="product-card__thumb-empty">
          {!hasImage ? '사진 없음' : visible && !failed ? '불러오는 중…' : '사진 없음'}
        </span>
      )}
      {children}
    </div>
  );
}

export default ProductCardThumb;
